import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';

// MUI Components
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardActionArea,
  Stack,
  GlobalStyles,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';

// Utils
import { MESSAGES } from '~/utils/message';
import { setItem } from '~/utils/local-storage';
import { getUserData } from '~/redux/actions/auth-actions';

// Components
import OrderFlow from './OrderFlow';
import OlcButton from '~/components/General/OlcButton';
import OlcTooltip from '~/components/General/OlcTooltip';
import FindNewContacts from './FindNewContacts';

// Images
import { MailContactsCIcon, UploadListCIcon, FindNewCIcon } from '~/assets/images';
import HomeSearchCIcon from '~/assets/images/create-order/home-search.svg';
import SearchAreaCIcon from '~/assets/images/create-order/search-area.svg';

// Styles
import './styles.scss';

const orderFlowStep = {
  addContacts: 0,
  mailerDetails: 1,
};

const FIND_NEW_CONTACTS_FEATURE_SLUGS = {
  accessAllowed: 'find_new_contacts_access_allowed',
  costPerRecord: 'find_new_contacts_cost_per_record',
};

const isEnabledFeatureValue = (value) =>
  value === true || value === 1 || value === '1' || value === 'true';

const FIND_NEW_OPTIONS = [
  {
    key: 'addresses',
    title: 'Radius Around an Address',
    description: 'Enter an address, find contacts within a radius around it.',
    bestFor: 'Reaching nearby homes or businesses that could use your services.',
    icon: HomeSearchCIcon,
  },
  {
    key: 'locations',
    title: 'Search an Area',
    description: 'Pick or draw an area, then filter for the contacts you want.',
    bestFor: 'Building a bigger list across a whole market.',
    icon: SearchAreaCIcon,
  },
];

const CreateOrder = ({ value, onChange, onNext, onCancel }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state?.userReducers?.user);
  const isTestMode = user?.isLiveMode === false;
  const {
    selectedContactIds = [],
    initialOrderOption = '',
    initialFindNewOption = '',
  } = location.state || {};
  const supportedInitialOrderOption = ['mail_contacts', 'upload_list', 'find_new'].includes(
    initialOrderOption
  )
    ? initialOrderOption
    : '';
  const supportedInitialFindNewOption = ['addresses', 'locations'].includes(initialFindNewOption)
    ? initialFindNewOption
    : '';
  const hasAppliedInitialFindNewOption = useRef(false);

  const [internalValue, setInternalValue] = useState(value || supportedInitialOrderOption);
  const [view, setView] = useState(() => {
    if (supportedInitialOrderOption === 'mail_contacts') return 'add_contacts';
    if (supportedInitialOrderOption === 'upload_list') return 'upload_list';
    if (supportedInitialOrderOption === 'find_new' && supportedInitialFindNewOption) {
      return 'find_new_pending';
    }
    return 'select';
  });
  const [findNewOption, setFindNewOption] = useState(supportedInitialFindNewOption);
  const [orderFlowInitialStep, setOrderFlowInitialStep] = useState(0);
  const [purchasedListReqId, setPurchasedListReqId] = useState(null);
  const [isRefreshingUser, setIsRefreshingUser] = useState(false);
  const [settingsRequestFailed, setSettingsRequestFailed] = useState(false);
  const features = Array.isArray(user?.features) ? user.features : [];
  const accessAllowedFeature = features.find(
    ({ slug }) => slug === FIND_NEW_CONTACTS_FEATURE_SLUGS.accessAllowed
  );
  const costPerRecordFeature = features.find(
    ({ slug }) => slug === FIND_NEW_CONTACTS_FEATURE_SLUGS.costPerRecord
  );
  const costPerRecord = Number(costPerRecordFeature?.value);
  const hasValidCost = Number.isFinite(costPerRecord) && costPerRecord > 0;
  const isSystemAdmin = user?.isSystemAdmin || user?.isSystemSuperAdmin;
  const hasAccessSetting = accessAllowedFeature?.value !== undefined || isSystemAdmin;
  const hasSettings = Boolean(user?.id) && hasValidCost && hasAccessSetting;
  const isFindNewContactsPlanAllowed =
    hasSettings && (isSystemAdmin || isEnabledFeatureValue(accessAllowedFeature?.value));
  const findNewContactsSettings = {
    error: settingsRequestFailed || (Boolean(user?.id) && !hasSettings),
    isAllowed: !isTestMode && isFindNewContactsPlanAllowed,
    loading: isRefreshingUser || !user?.id,
    costPerRecord: hasValidCost ? costPerRecord : null,
    costPerRecordCents: hasValidCost ? Math.round(costPerRecord * 100) : null,
  };
  const selectedValue = value !== undefined ? value : internalValue;
  const canContinue =
    selectedValue &&
    (selectedValue !== 'find_new' || (findNewOption && findNewContactsSettings.isAllowed));

  const loadFindNewContactsSettings = useCallback(async () => {
    setSettingsRequestFailed(false);
    setIsRefreshingUser(true);

    const response = await dispatch(getUserData());
    const refreshedUser = response?.data?.data;
    setSettingsRequestFailed(!refreshedUser?.id);
    setIsRefreshingUser(false);

    return refreshedUser || null;
  }, [dispatch]);

  useLayoutEffect(() => {
    if (
      hasAppliedInitialFindNewOption.current ||
      supportedInitialOrderOption !== 'find_new' ||
      findNewContactsSettings.loading
    ) {
      return;
    }

    hasAppliedInitialFindNewOption.current = true;
    if (!isTestMode && findNewContactsSettings.isAllowed && supportedInitialFindNewOption) {
      setView('find_new');
      return;
    }

    setInternalValue('');
    setFindNewOption('');
    setView('select');
  }, [
    findNewContactsSettings.isAllowed,
    findNewContactsSettings.loading,
    isTestMode,
    supportedInitialFindNewOption,
    supportedInitialOrderOption,
  ]);

  useEffect(() => {
    if (view === 'select') return;

    requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;

      document.querySelectorAll('#dashboard, .mainContent').forEach((element) => {
        element.scrollTop = 0;
      });
    });
  }, [view]);

  useEffect(() => {
    if (!user?.id || !isTestMode || (view !== 'find_new' && view !== 'find_new_pending')) return;

    setInternalValue('');
    setFindNewOption('');
    setView('select');
  }, [isTestMode, user?.id, view]);

  // Default cancel handler that navigates to orders listing
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate('/orders');
    }
  };

  // Handler to go back to selection screen from OrderFlow
  const handleBackToSelection = () => {
    setOrderFlowInitialStep(orderFlowStep.addContacts);
    setView('select');
  };

  const handleBackToContacts = () => navigate('/contacts');

  const OPTIONS = useMemo(
    () => [
      {
        key: 'mail_contacts',
        title: 'Mail My Contacts',
        description: 'Pick from contacts already saved in your account.',
        icon: MailContactsCIcon,
      },
      {
        key: 'upload_list',
        title: 'Upload a List',
        description: 'Import a CSV of addresses to mail.',
        icon: UploadListCIcon,
      },
      {
        key: 'find_new',
        title: 'Find New Contacts',
        description:
          'Search by address or area, then filter to build a targeted list of new contacts to mail.',
        icon: FindNewCIcon,
      },
    ],
    []
  );

  const handleSelect = (key) => {
    if (key === 'find_new' && !findNewContactsSettings.isAllowed) return;

    if (onChange) onChange(key);
    if (value === undefined) setInternalValue(key);
    if (key !== 'find_new' || selectedValue !== 'find_new') setFindNewOption('');
    // Only update selection, don't navigate yet
  };

  const handleNext = () => {
    if (!canContinue) return;
    setOrderFlowInitialStep(orderFlowStep.addContacts);
    setPurchasedListReqId(null);

    // Determine view based on selected option
    let nextView = 'select';
    switch (selectedValue) {
      case 'mail_contacts':
        nextView = 'add_contacts';
        break;
      case 'upload_list':
        nextView = 'upload_list';
        break;
      case 'find_new':
        nextView = 'find_new';
        break;
    }

    // If onNext callback exists, call it with the selected value
    if (onNext) {
      onNext(selectedValue);
    }

    // Navigate to OrderFlow if applicable
    if (nextView !== 'select') {
      setView(nextView);
    }
  };

  // Any pre-selected contacts (single or multiple) skip the selection screen
  // and land directly on the Add Contacts step with those contacts pre-filled.
  if (selectedContactIds.length >= 1) {
    return (
      <OrderFlow
        onCancel={handleBackToContacts}
        onBack={handleBackToContacts}
        view="add_contacts"
        preSelectedContactIds={selectedContactIds}
      />
    );
  }

  if (view === 'add_contacts' || view === 'upload_list') {
    return (
      <OrderFlow
        initialStep={orderFlowInitialStep}
        key={`${view}-${orderFlowInitialStep}-${purchasedListReqId || 'manual'}`}
        initialReqId={purchasedListReqId}
        onCancel={handleCancel}
        onBack={handleBackToSelection}
        view={view}
      />
    );
  }

  if (view === 'find_new_pending') {
    return (
      <Container maxWidth="xxl" className="createOrderContainer">
        <Box
          className="createOrderIsland createOrderInitialLoading"
          role="status"
          aria-live="polite"
        >
          <Typography>Opening list builder...</Typography>
        </Box>
      </Container>
    );
  }

  if (view === 'find_new') {
    return (
      <FindNewContacts
        costPerRecord={findNewContactsSettings.costPerRecord}
        costPerRecordCents={findNewContactsSettings.costPerRecordCents}
        findNewMode={findNewOption}
        onCancel={handleCancel}
        onPricingChanged={loadFindNewContactsSettings}
        onBack={handleBackToSelection}
        onContinue={(_, action, purchaseData = {}) => {
          if (action === 'exit') {
            handleCancel();
            return;
          }

          if (purchaseData.reqId) {
            setItem('reqId', purchaseData.reqId);
            setPurchasedListReqId(purchaseData.reqId);
          }

          if (action === 'continue_to_mail') {
            setOrderFlowInitialStep(orderFlowStep.mailerDetails);
            setView('upload_list');
          }
        }}
      />
    );
  }

  return (
    <Container maxWidth="xxl" className="createOrderContainer">
      <Box className="createOrderIsland">
        <Box className="createOrderHeader">
          <Typography className="pageTitle">{MESSAGES?.ORDERS?.CREATE?.TITLE}</Typography>
          <Typography className="pageDescription">Who do you want to mail?</Typography>
        </Box>

        <Box className="cardsWrapper">
          {OPTIONS.map((opt) => {
            const isSelected = selectedValue === opt.key;
            const isFindNewContactsOption = opt.key === 'find_new';
            const isDisabled = isFindNewContactsOption && !findNewContactsSettings.isAllowed;
            const hasSettingsError = isFindNewContactsOption && findNewContactsSettings.error;
            const disabledTooltip =
              isFindNewContactsOption && isTestMode ? MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE : '';
            const description = isFindNewContactsOption
              ? findNewContactsSettings.loading
                ? 'Checking plan availability...'
                : findNewContactsSettings.error
                  ? 'Unable to load Find New Contacts availability.'
                  : isTestMode
                    ? MESSAGES.MODE.LIVE_MODE_ONLY_FEATURE
                    : isDisabled
                      ? 'Find New Contacts is not available on your current plan.'
                      : opt.description
              : opt.description;

            return (
              <OlcTooltip
                arrow
                disableInteractive={!disabledTooltip}
                key={opt.key}
                placement="top"
                title={disabledTooltip}
              >
                <Box
                  aria-disabled={isDisabled && !hasSettingsError ? true : undefined}
                  className={`optionCardWrapper ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''} ${hasSettingsError ? 'settingsError' : ''}`}
                  role={hasSettingsError ? undefined : 'button'}
                  tabIndex={isDisabled ? -1 : 0}
                  onClick={() => handleSelect(opt.key)}
                  onKeyDown={(e) => {
                    if (!isDisabled && (e.key === 'Enter' || e.key === ' ')) handleSelect(opt.key);
                  }}
                >
                  <img src={opt.icon} alt={opt.title} />
                  <Typography className="optionTitle">{opt.title}</Typography>
                  {hasSettingsError ? (
                    <Box className="findNewSettingsError">
                      <Typography className="optionDescription">{description}</Typography>
                      <Tooltip
                        arrow
                        disableInteractive
                        placement="top"
                        title="Retry loading availability"
                      >
                        <IconButton
                          aria-label="Retry loading Find New Contacts availability"
                          className="findNewSettingsRetry"
                          onClick={loadFindNewContactsSettings}
                        >
                          <ReplayOutlinedIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  ) : (
                    <Typography className="optionDescription">{description}</Typography>
                  )}
                </Box>
              </OlcTooltip>
            );
          })}
        </Box>

        {selectedValue === 'find_new' && (
          <Box className="findNewOptionsPanel" aria-label="Find new contacts options">
            <Typography className="findNewOptionsTitle">
              How do you want to find contacts?
            </Typography>
            <Box className="findNewOptionsGrid">
              {FIND_NEW_OPTIONS.map(({ key, title, description, bestFor, icon }) => (
                <button
                  className={
                    findNewOption === key ? 'findNewOptionCard selected' : 'findNewOptionCard'
                  }
                  key={key}
                  onClick={() => setFindNewOption(key)}
                  aria-pressed={findNewOption === key}
                  type="button"
                >
                  <img className="findNewOptionIcon" src={icon} alt={title} />
                  <span className="findNewOptionCopy">
                    <strong>{title}</strong>
                    <small>{description}</small>
                    <small className="findNewOptionBestFor">
                      <span>Best for:</span>
                      {bestFor}
                    </small>
                  </span>
                </button>
              ))}
            </Box>
          </Box>
        )}

        <Box className="orderActions">
          <OlcButton variant="secondary" onClick={handleCancel}>
            Cancel
          </OlcButton>
          <OlcButton variant="primary" onClick={handleNext} disabled={!canContinue}>
            Next
          </OlcButton>
        </Box>
      </Box>
    </Container>
  );
};

export default CreateOrder;
